import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Phone, Mail, Globe, Eye, Copy, Check, BarChart3, Users, Sparkles, Pin } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { VerifiedUnavailable } from "@/components/VerifiedUnavailable";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAnalyst } from "@/context/AnalystContext";
import { addRecent } from "@/lib/recentListings";
import InvestmentValuation from "@/components/InvestmentValuation";
import WatchlistButton from "@/components/WatchlistButton";
import { FreshnessNote } from "@/components/FreshnessNote";
import CopyStat from "@/components/CopyStat";
import { copyText } from "@/lib/clipboard";
import { bandForPrice } from "@/lib/bands";

const HOME_METRIC_LABELS = {
  band_median_price: "Band Median", price_vs_median_pct: "Price vs. Band Median",
  band_median_ppsf: "Band Median $/sqft", ppsf: "This $/sqft",
  ppsf_vs_median_pct: "$/sqft vs. Median", days_on_market: "Days on Market",
};

const fmt = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");
const Stat = ({ label, value, testId }) => (
  <div className="metric-empty p-4" data-testid={testId}>
    <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono-data text-lg mt-1">{value ?? "—"}</div>
  </div>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setContext, ask, pinToSlot } = useAnalyst();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [val, setVal] = useState(null);
  const rid = decodeURIComponent(id);

  useEffect(() => {
    setLoading(true); setErr(false);
    api.get(`/properties/detail`, { params: { id: rid } })
      .then((r) => setData(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [rid]);

  useEffect(() => {
    if (data?.listing) {
      const l = data.listing;
      addRecent("home", {
        id: l.id, title: l.address, subtitle: [l.city, l.state].filter(Boolean).join(", "),
        price: l.price, property_type: l.property_type, photo: (l.photos || [])[0] || null,
      });
      api.post("/activity/view", { item_type: "property", item_id: l.id }).catch(() => {});
      setContext({
        item_id: l.id, item_type: "property", title: l.address,
        address: l.address, price: l.price, price_per_sqft: l.price_per_sqft, beds: l.beds,
        baths: l.baths, sqft: l.sqft, year_built: l.year_built, days_on_market: l.days_on_market,
        mls_number: l.mls_number, property_type: l.property_type,
        valuation: data.valuation, local_market: data.market, price_history: l.history,
      });
    }
    return () => setContext(null);
  }, [data, setContext]);

  useEffect(() => {
    if (!data?.listing) return;
    setVal(null);
    api.post("/properties/valuation", { property: data.listing })
      .then((r) => setVal(r.data))
      .catch((e) => setVal(e?.response?.status === 402 ? { locked: true } : { available: false }));
  }, [data]);

  useEffect(() => {
    if (val && !val.locked && val.available) {
      setContext((prev) => ({
        ...(prev || {}),
        investment_valuation: { score: val.investment_score, verdict: val.verdict, band: val.band, metrics: val.metrics, factors: val.factors },
        market_intelligence_band: val.band_context,
      }));
    }
  }, [val, setContext]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse">Loading verified listing…</div>;
  if (err || !data?.listing) return <div className="max-w-3xl mx-auto px-5 py-20"><VerifiedUnavailable note="This listing is no longer available — it may have recently sold or been withdrawn. It has been removed from our verified corpus." /></div>;

  const l = data.listing;
  const agent = l.agent || {};
  const office = l.office || {};
  const history = (l.history || []).filter((h) => h.price).map((h) => ({ date: h.date, price: h.price }));

  const summary = [
    l.address,
    [l.city, l.state, l.zip].filter(Boolean).join(", "),
    l.price ? `$${Number(l.price).toLocaleString()}` : null,
    l.mls_number ? `MLS ${l.mls_number}` : null,
  ].filter(Boolean).join("\n");
  const reBand = bandForPrice("re", l.price);
  const marketUrl = reBand
    ? `/market?tab=real-estate&city=${encodeURIComponent(l.city || "")}&state=${encodeURIComponent(l.state || "")}&band=${reBand.key}`
    : null;

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8">
      <button onClick={() => navigate(-1)} data-testid="back-button" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to results
      </button>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="mt-2">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <MapPin className="w-4 h-4" /> {[l.city, l.state, l.zip].filter(Boolean).join(", ")}
              {l.status && <span className="ml-2 font-mono-data text-[10px] uppercase tracking-wider text-emerald-500 border border-emerald-500/30 rounded-full px-2 py-0.5">{l.status}</span>}
            </div>
            <h1 className="font-editorial text-4xl sm:text-5xl italic leading-tight" data-testid="property-address">{l.address}</h1>
            <div className="font-mono-data text-3xl mt-4" data-testid="property-price">{fmt(l.price)}</div>
            <FreshnessNote lastSeen={l._last_seen} className="mt-2" />
            <button
              onClick={() => ask?.(`Is this home fairly priced? ${l.address}`, { item_type: "property", item_id: l.id })}
              data-testid="ask-ai-about-listing"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 text-accent px-4 py-2 text-sm hover:bg-accent/20 transition-colors">
              <Sparkles className="w-4 h-4" /> Ask AI about this listing
            </button>
            <div className="mt-4 ml-2 inline-flex items-center gap-2">
              <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Pin to compare</span>
              {["A", "B"].map((s) => (
                <button key={s} type="button"
                  onClick={() => { pinToSlot?.(s, { item_type: "property", item_id: l.id, title: l.address }); toast.success(`Pinned to ${s} — open Vaelith AI to compare A vs B`); }}
                  data-testid={`pin-to-${s.toLowerCase()}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-border text-sm font-mono-data font-semibold hover:border-accent/50 hover:text-accent transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
            <Stat label="Beds" value={l.beds} testId="stat-beds" />
            <Stat label="Baths" value={l.baths} testId="stat-baths" />
            <Stat label="Sq Ft" value={l.sqft ? Number(l.sqft).toLocaleString() : "—"} testId="stat-sqft" />
            <Stat label="$ / Sq Ft" value={l.price_per_sqft ? `$${l.price_per_sqft}` : "—"} testId="stat-ppsf" />
            <Stat label="Lot Size" value={l.lot_size ? `${Number(l.lot_size).toLocaleString()} sqft` : "—"} />
            <Stat label="Year Built" value={l.year_built} />
            <Stat label="Days on Market" value={l.days_on_market} />
            <Stat label="Type" value={l.property_type} />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <CopyStat label="MLS Number" value={l.mls_number} testId="stat-mls" />
            <Stat label="MLS Source" value={l.mls_name} />
          </div>

          <section className="mt-10">
            <h2 className="font-display text-2xl font-light tracking-tight mb-4">Price History</h2>
            {history.length > 1 ? (
              <div className="bg-surface border border-border rounded-xl p-6 h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history}>
                    <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
                    <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontFamily: "JetBrains Mono", fontSize: 12, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                    <Line type="monotone" dataKey="price" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : history.length === 1 ? (
              <div className="bg-surface border border-border rounded-xl p-6 font-mono-data text-sm">
                Listed {history[0].date} at {fmt(history[0].price)}
              </div>
            ) : <VerifiedUnavailable testId="history-unavailable" note="No verified price history recorded for this listing." />}
          </section>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-surface border border-border rounded-xl p-5">
            <WatchlistButton itemType="property" itemId={l.id} data={{ title: l.address, price: l.price }} className="w-full mb-3" />
            <Button variant="outline" data-testid="copy-summary-button" className="rounded-full w-full mb-3"
              onClick={async () => { (await copyText(summary)) ? toast.success("Listing details copied") : toast.error("Could not copy"); }}>
              <Copy className="w-4 h-4 mr-2" />Copy listing details
            </Button>
            {marketUrl && (
              <Button variant="outline" data-testid="view-market-band-button" className="rounded-full w-full mb-3"
                onClick={() => navigate(marketUrl)} title={`See the ${reBand.label} band for ${l.city}`}>
                <BarChart3 className="w-4 h-4 mr-2" />View in Market Intelligence
              </Button>
            )}

            <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Listing Agent</div>
            {agent.name ? (
              <div data-testid="listing-agent">
                <div className="font-display font-medium text-lg">{agent.name}</div>
                {office.name && <div className="text-sm text-muted-foreground mt-0.5">{office.name}</div>}
                {agent.phone && <div className="text-sm text-muted-foreground mt-2 flex items-center gap-2" data-testid="agent-phone"><Phone className="w-3.5 h-3.5 shrink-0" />{agent.phone}</div>}
                {agent.email && <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 break-all" data-testid="agent-email"><Mail className="w-3.5 h-3.5 shrink-0" />{agent.email}</div>}
                {office.phone && !agent.phone && <div className="text-sm text-muted-foreground mt-2 flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" />{office.phone}</div>}
              </div>
            ) : <VerifiedUnavailable testId="agent-unavailable" note="No verified agent information." />}
          </div>

          <InvestmentValuation val={val} metricLabels={HOME_METRIC_LABELS} />

          <div className="bg-surface border border-border rounded-xl p-5">
            <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Location</div>
            {l.latitude != null && l.longitude != null ? (
              <div data-testid="location-block">
                <div className="font-mono-data text-sm">{l.latitude.toFixed(5)}, {l.longitude.toFixed(5)}</div>
              </div>
            ) : <VerifiedUnavailable note="No verified coordinates." />}
          </div>
        </aside>
      </div>
    </div>
  );
}
